import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { map } from 'rxjs';
import { Shot, ShotApprovalStatus } from 'src/app/components/company/models/new/shot';
import {
	MediaShareColorShots,
	MediaShareDetail
} from 'src/app/components/company/models/new/media-share-detail';
import { MediaShareDetailMapper } from 'src/app/components/company/services/media-share-detail-mapper';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { ImageGalleryComponent } from 'src/app/shared/components/image-gallery/image-gallery.component';
import { SharedQueries } from 'src/app/shared/types/get-query';
import { ImageService } from 'src/app/services/image.service';

@Component({
	selector: 'bt-share-media',
	standalone: false,
	templateUrl: './share-media.component.html',
	styleUrl: './share-media.component.scss'
})
export class ShareMediaComponent implements OnInit {
	readonly #route = inject(ActivatedRoute);
	readonly #graphqlService = inject(GraphQLService);
	readonly #destroyRef = inject(DestroyRef);
	readonly #dialog = inject(MatDialog);
	readonly #mediaShareDetailMapper = inject(MediaShareDetailMapper);
	readonly #imageService = inject(ImageService);
	
	loading = false;
	enabled = true;
	mediaShareDetails: Array<MediaShareDetail> = [];
	ShotApprovalStatus = ShotApprovalStatus;
	id: string = '';

	ngOnInit(): void {
		const id = this.#route.snapshot.paramMap.get('id') ?? '';
		this.id = id;
		this.getMedia(id);
	}

	getMedia(id: string) {
		this.loading = true;
		this.#graphqlService
			.getData(SharedQueries.GET_MEDIA_SHARE_DETAILS, { mediaShareLinkId: id })
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['mediaShareLinkDetails'])
			)
			.subscribe({
				next: (media) => {
					this.enabled = media?.enabled !== false;
					this.mediaShareDetails = this.enabled
						? this.#mediaShareDetailMapper.toMediaShareDetails(media?.styles)
						: [];
					this.loading = false;
				},
				error: () => {
					this.loading = false;
				}
			});
	}

	toggle(product: MediaShareDetail) {
		product.opened = !product.opened;
	}

	getShotsWithImages(colorShots: MediaShareColorShots): Shot[] {
		return colorShots.shots?.filter((shot) => shot.imgPath) || [];
	}

	viewGallery(shots: Shot[], first?: number): void {
		if (!shots.length) {
			return;
		}

		this.#dialog.open(ImageGalleryComponent, {
			data: { shots, first }
		});
	}

	isVideo(shot: Shot): boolean {
		if (!shot.imgPath) {
			return false;
		}

		const path = shot.imgPath.toLowerCase();
		return path.lastIndexOf('.mp4') > 0 || path.lastIndexOf('.mkv') > 0;
	}

	downloadImagesByMediaShareLinkId() {
		if (!this.id) {
			return;
		}

		this.loading = true;
		this.#imageService
			.downloadImagesByMediaShareLinkId(this.id)
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: (res: Blob) => {
					this.loading = false;
					const url = window.URL.createObjectURL(res);
					const a = document.createElement('a');
					a.href = url;
					a.download = `Images_MediaShareLink_${this.id}.zip`;
					a.click();
					window.URL.revokeObjectURL(url);
				},
				error: () => {
					this.loading = false;
				}
			});
	}
}
